import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import MobileTabNavigation from './MobileTabNavigation';
import { AgentsContainer } from './Agent';
import { UsersContainer } from './User';
import { DocumentsContainer } from './Document';
import { ModelsContainer } from './Model';
import { ConfirmDialog } from '../Notification';
import { LazyLoadingSpinner } from '../common';
import { useAdminTranslation } from '../../modules/i18n';

// Lazy load heavy KG components
const UnifiedKGManagement = lazy(() => import('./KnowledgeGraph/UnifiedKGManagement'));

interface AdminProps {
  defaultTab?: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models';
}


const Admin: React.FC<AdminProps> = ({ defaultTab = 'agents' }) => {
  const { t } = useAdminTranslation();
  const [activeTab, setActiveTab] = useState<
    'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models'
  >(defaultTab);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    isLoading?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isLoading: false
  });

  const navigate = useNavigate();

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle tab change and navigation
  const handleTabChange = (tab: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models') => {
    setActiveTab(tab);
    const routeMap = {
      agents: '/admin/agents',
      users: '/admin/users',
      documents: '/admin/documents',
      knowledgeGraph: '/admin/knowledge-graph',
      models: '/admin/models'
    };
    navigate(routeMap[tab]);
  };

  return (
    <div className='flex h-[calc(100vh-4rem)] flex-col bg-gray-100 dark:bg-dark-background'>
      {/* Mobile tab navigation */}
      <MobileTabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Main content area */}
      {isMobile ? (
        // Mobile: Tab-based layout
        <div className='flex min-h-0 flex-1 flex-col'>
          <div className='m-0 min-h-0 flex-1 overflow-auto rounded-none border border-gray-200 bg-white shadow-sm dark:border-border-dark dark:bg-dark-background-secondary md:rounded-xl scrollbar-thin'>
            {activeTab === 'agents' && (
              <div className='p-4'>
                <AgentsContainer />
              </div>
            )}
            {activeTab === 'users' && (
              <div className='p-4'>
                <UsersContainer confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
              </div>
            )}
            {activeTab === 'documents' && (
              <div className='p-4'>
                <DocumentsContainer confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
              </div>
            )}
            {activeTab === 'knowledgeGraph' && (
              <div className='p-4'>
                <Suspense fallback={<LazyLoadingSpinner message={t('loading.knowledgeGraph')} />}>
                  <UnifiedKGManagement />
                </Suspense>
              </div>
            )}
            {activeTab === 'models' && (
              <div className='p-4'>
                <ModelsContainer />
              </div>
            )}
          </div>
        </div>
      ) : (
        // Desktop: Sidebar + Main content layout
        <div className='flex min-h-0 flex-1 gap-4 p-4 '>
          {/* Left Sidebar - Admin Navigation */}
          <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} flex-shrink-0 transition-all duration-300`}>
            <AdminSidebar
              isVisible={true}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              hideHeader={false}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              className='h-full rounded-xl shadow-sm'
            />
          </div>

          {/* Main Content Area */}
          <div className='flex min-w-0 flex-1 flex-col'>
            <div className='h-full rounded-xl border border-gray-200 bg-white shadow-sm dark:border-border-dark dark:bg-dark-background-secondary'>
              <div className='h-full overflow-auto'>
                {activeTab === 'agents' && (
                  <div className='h-full p-6'>
                    <AgentsContainer />
                  </div>
                )}
                {activeTab === 'users' && (
                  <div className='h-full p-6'>
                    <UsersContainer confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
                  </div>
                )}
                {activeTab === 'documents' && (
                  <div className='h-full p-6'>
                    <DocumentsContainer confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
                  </div>
                )}
                {activeTab === 'knowledgeGraph' && (
                  <div className='h-full p-6'>
                    <Suspense fallback={<LazyLoadingSpinner message={t('loading.knowledgeGraph')} />}>
                      <UnifiedKGManagement />
                    </Suspense>
                  </div>
                )}
                {activeTab === 'models' && (
                  <div className='h-full p-6'>
                    <ModelsContainer />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All modals are now managed by respective Container components */}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
        isLoading={confirmDialog.isLoading}
      />
    </div>
  );
};

export default Admin;
